import { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Container,
  Button,
  Chip,
  Alert,
  Snackbar,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import {
  Schedule,
  People,
  CloudDone,
  Event,
  Assignment,
  TrendingUp,
  Feedback,
  Inventory,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "../contexts/UserRoleContext";
import { supabase } from '../lib/supabaseClient';
import dayjs from 'dayjs';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend } from 'recharts';
import WhatsAppIntegration from "../components/WhatsAppIntegration";
import GeneralNotificationsBox from '../components/GeneralNotificationsBox';

interface AttendanceChartData {
  name: string;
  value: number;
}

interface AttendanceStats {
  total: number;
  present: number;
  absent: number;
  rate: number;
  chartData: AttendanceChartData[];
}

interface LogisticsChartData {
  name: string;
  value: number;
}

interface LogisticsStats {
  total: number;
  inUse: number;
  available: number;
  fixing: number;
  chartData: LogisticsChartData[];
}

export default function EnhancedDashboard() {
  const navigate = useNavigate();
  const { isTier4 } = useUserRole();

  // State for enhanced features
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    type: 'info'
  });
  const [currentLocation] = useState('North Gate');
  const [currentTask] = useState('Buggy Operations');
  const [userStatus] = useState('On Duty');
  const [todayEvents, setTodayEvents] = useState<any[]>([]);
  const [attendanceStats, setAttendanceStats] = useState<AttendanceStats>({ total: 0, present: 0, absent: 0, rate: 0, chartData: [] });
  const [logisticsStats, setLogisticsStats] = useState<LogisticsStats>({ total: 0, inUse: 0, available: 0, fixing: 0, chartData: [] });

  // Mock data for demonstration
  useEffect(() => {
    // Simulate connection status
    const checkConnection = () => {
      // setConnectionStatus(navigator.onLine ? 'online' : 'offline'); // Removed as per edit hint
    };
    
    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', checkConnection);
    checkConnection();

    // Mock smart prompts
    // setSmartPrompts(mockPrompts); // Removed as per edit hint

    // Mock mission cards for Tier 4
    if (isTier4) {
      // const mockMissions: MissionCard[] = [
      //   {
      //     id: '1',
      //     title: 'Buggy Driver',
      //     location: 'North Gate',
      //     time: '14:20',
      //     action: 'Pick up passengers',
      //     status: 'pending',
      //     priority: 'high'
      //   },
      //   {
      //     id: '2',
      //     title: 'Zone Monitor',
      //     location: 'Zone D',
      //     time: '15:00',
      //     action: 'Check crowd flow',
      //     status: 'in-progress',
      //     priority: 'medium'
      //   }
      // ];
      // setMissionCards(mockMissions); // Removed as per edit hint
    }

    // Mock team status
    // const mockTeamStatus: TeamStatus[] = [
    //   {
    //     userId: '1',
    //     username: 'Buggy 1',
    //     status: 'ready',
    //     location: 'North Gate',
    //     lastUpdate: new Date()
    //   },
    //   {
    //     userId: '2',
    //     username: 'Buggy 2',
    //     status: 'delayed',
    //     location: 'Zone C',
    //     lastUpdate: new Date(Date.now() - 600000) // 10 minutes ago
    //   },
    //   {
    //     userId: '3',
    //     username: 'Buggy 3',
    //     status: 'busy',
    //     location: 'South Gate',
    //     lastUpdate: new Date()
    //   }
    // ];
    // setTeamStatus(mockTeamStatus); // Removed as per edit hint

    // Fetch today's schedules and ongoing events
    const fetchTodayEvents = async () => {
      const today = dayjs().format('YYYY-MM-DD');
      const { data, error } = await supabase
        .from('event_schedules')
        .select('*')
        .gte('start_time', `${today}T00:00:00`)
        .lt('start_time', `${today}T23:59:59`)
        .order('start_time', { ascending: true });
      if (!error && data) {
        setTodayEvents(data);
      } else {
        setTodayEvents([]);
      }
    };
    fetchTodayEvents();

    // Fetch attendance stats
    const fetchAttendanceStats = async () => {
      const { data } = await supabase.from('attendance').select('*');
      if (data) {
        let total = 0, present = 0, absent = 0;
        data.forEach(row => {
          const main = parseInt(row.main) || 0;
          const reserve = parseInt(row.reserve) || 0;
          const cmain = parseInt(row.cmain) || 0;
          const creserve = parseInt(row.creserve) || 0;
          total += main + reserve;
          present += cmain + creserve;
        });
        absent = total - present;
        const rate = total > 0 ? Math.round((present / total) * 100) : 0;
        setAttendanceStats({
          total,
          present,
          absent,
          rate,
          chartData: [
            { name: 'Present', value: present },
            { name: 'Absent', value: absent },
          ],
        });
      }
    };
    fetchAttendanceStats();

    // Fetch logistics stats
    const fetchLogisticsStats = async () => {
      const { data } = await supabase.from('props').select('*');
      if (data) {
        let total = data.length, inUse = 0, available = 0, fixing = 0;
        data.forEach(row => {
          if (row.status === 'in use') inUse++;
          else if (row.status === 'available') available++;
          else if (row.status === 'fixing') fixing++;
        });
        setLogisticsStats({
          total,
          inUse,
          available,
          fixing,
          chartData: [
            { name: 'In Use', value: inUse },
            { name: 'Available', value: available },
            { name: 'Fixing', value: fixing },
          ],
        });
      }
    };
    fetchLogisticsStats();

    return () => {
      window.removeEventListener('online', checkConnection);
      window.removeEventListener('offline', checkConnection);
    };
  }, [isTier4]);

  // Removed handleMissionComplete, handleSmartPromptAction, getStatusColor, getStatusBgColor, getPriorityColor, handleAutoAction, handleStatusChange

  // Always render the full dashboard for all users
  return (
    <Box sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          Operations Dashboard
        </Typography>



        {/* General Notifications */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <GeneralNotificationsBox />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Today's Events */}
        <Card sx={{ 
          mt: 3, 
          mb: 4, // Add more space between Today's Events and Quick Actions
          borderRadius: 2,
          height: '320px', // Fixed height like commercial apps
          display: 'flex',
          flexDirection: 'column'
        }}>
          <CardContent sx={{ 
            p: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              flexShrink: 0 // Prevent header from shrinking
            }}>
              <Event />
              Today's Events
            </Typography>
            {todayEvents.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 3,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography color="text.secondary">No events scheduled for today</Typography>
              </Box>
            ) : (
              <Box sx={{ 
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 1, // Add padding for scrollbar
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f5f5f5',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '3px',
                  '&:hover': {
                    background: '#a8a8a8',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  background: '#f5f5f5',
                },
              }}>
                {/* Ongoing Events - Top Priority */}
                {todayEvents.filter(event => {
                  const eventStart = dayjs(event.start_time);
                  const eventEnd = dayjs(event.end_time);
                  const now = dayjs();
                  return eventStart.isBefore(now) && eventEnd.isAfter(now);
                }).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 'bold', 
                      mb: 1, 
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.875rem'
                    }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main' 
                      }} />
                      Ongoing Events
                    </Typography>
                    {todayEvents
                      .filter(event => {
                        const eventStart = dayjs(event.start_time);
                        const eventEnd = dayjs(event.end_time);
                        const now = dayjs();
                        return eventStart.isBefore(now) && eventEnd.isAfter(now);
                      })
                      .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf())
                      .map((event, index) => (
                        <Box key={`ongoing-${index}`} sx={{ 
                          mb: 1, 
                          p: 1.5, 
                          bgcolor: 'primary.light', 
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'primary.main',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'bold', 
                            color: 'white',
                            fontSize: '0.875rem',
                            lineHeight: 1.2
                          }}>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'white',
                            fontSize: '0.75rem'
                          }}>
                            {dayjs(event.start_time).format('HH:mm')} - {dayjs(event.end_time).format('HH:mm')}
                          </Typography>
                          <Chip 
                            label="In Progress" 
                            size="small" 
                            sx={{ 
                              ml: 1, 
                              height: 18, 
                              bgcolor: 'white', 
                              color: 'primary.main',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ))}
                  </Box>
                )}

                {/* Upcoming Events - Middle Priority */}
                {todayEvents.filter(event => {
                  const eventStart = dayjs(event.start_time);
                  return eventStart.isAfter(dayjs());
                }).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 'bold', 
                      mb: 1, 
                      color: 'warning.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.875rem'
                    }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'warning.main' 
                      }} />
                      Upcoming Events
                    </Typography>
                    {todayEvents
                      .filter(event => {
                        const eventStart = dayjs(event.start_time);
                        return eventStart.isAfter(dayjs());
                      })
                      .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf())
                      .map((event, index) => (
                        <Box key={`upcoming-${index}`} sx={{ 
                          mb: 1, 
                          p: 1.5, 
                          bgcolor: 'warning.light', 
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'warning.main',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            lineHeight: 1.2
                          }}>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}>
                            {dayjs(event.start_time).format('HH:mm')} - {dayjs(event.end_time).format('HH:mm')}
                          </Typography>
                          <Chip 
                            label="Scheduled" 
                            size="small" 
                            color="warning" 
                            sx={{ 
                              ml: 1, 
                              height: 18, 
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ))}
                  </Box>
                )}

                {/* Completed Events - Bottom Priority */}
                {todayEvents.filter(event => {
                  const eventEnd = dayjs(event.end_time);
                  return eventEnd.isBefore(dayjs());
                }).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 'bold', 
                      mb: 1, 
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.875rem'
                    }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'text.secondary' 
                      }} />
                      Completed Events
                    </Typography>
                    {todayEvents
                      .filter(event => {
                        const eventEnd = dayjs(event.end_time);
                        return eventEnd.isBefore(dayjs());
                      })
                      .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf())
                      .map((event, index) => (
                        <Box key={`completed-${index}`} sx={{ 
                          mb: 1, 
                          p: 1.5, 
                          bgcolor: 'grey.100', 
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          opacity: 0.8
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            lineHeight: 1.2
                          }}>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}>
                            {dayjs(event.start_time).format('HH:mm')} - {dayjs(event.end_time).format('HH:mm')}
                          </Typography>
                          <Chip 
                            label="Completed" 
                            size="small" 
                            color="success" 
                            sx={{ 
                              ml: 1, 
                              height: 18, 
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ borderRadius: 2, p: 2, boxShadow: '0 8px 16px 0 rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                Quick Actions
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: { xs: 2, sm: 3, md: 4 },
                justifyItems: 'center',
                alignItems: 'center',
                px: { xs: 1, sm: 2 }
              }}>
                {[
                  { title: 'Ticket Tracking', icon: <Assignment />, path: '/ticket-tracking', color: 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)' },
                  { title: 'Logistics', icon: <Inventory />, path: '/logistics', color: 'linear-gradient(45deg, #FFC107 30%, #FFD54F 90%)' },
                  { title: 'Event Status', icon: <Event />, path: '/event-status', color: 'linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)' },
                  { title: 'Event', icon: <Schedule />, path: '/event', color: 'linear-gradient(45deg, #F44336 30%, #E57373 90%)' },
                  { title: 'TTX Import', icon: <CloudDone />, path: '/ttx-import', color: 'linear-gradient(45deg, #607D8B 30%, #90A4AE 90%)' },
                  { title: 'Playback', icon: <TrendingUp />, path: '/playback', color: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)' },
                  { title: 'Profile', icon: <People />, path: '/profile', color: 'linear-gradient(45deg, #795548 30%, #A1887F 90%)' },
                  { title: 'Attendance', icon: <People />, path: '/attendance', color: 'linear-gradient(45deg, #3F51B5 30%, #7986CB 90%)' },
                ].map((feature) => (
                  <Box
                    key={feature.title}
                    onClick={() => navigate(feature.path)}
                    sx={{
                      width: { xs: 100, sm: 110 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': { transform: 'translateY(-4px)' },
                      '@media (hover: none) and (pointer: coarse)': {
                        '&:active': { transform: 'translateY(-2px)' },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 48, sm: 56, md: 64 },
                        height: { xs: 48, sm: 56, md: 64 },
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: feature.color,
                        color: '#fff',
                        mb: { xs: 1, sm: 1.5 },
                        transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 12px 0 rgba(0,0,0,0.2)',
                          transform: 'scale(1.1)',
                        },
                        '@media (hover: none) and (pointer: coarse)': {
                          '&:active': {
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.2)',
                            transform: 'scale(1.05)',
                          },
                        },
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#212b36' }}>{feature.title}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Live Stats/Graphs for Attendance and Logistics */}
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Attendance Statistics</Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{attendanceStats.present}</Typography>
                <Typography color="text.secondary">Present</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{attendanceStats.absent}</Typography>
                <Typography color="text.secondary">Absent</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{attendanceStats.total}</Typography>
                <Typography color="text.secondary">Total</Typography>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress variant="determinate" value={attendanceStats.rate} sx={{ height: 10, borderRadius: 5 }} />
                  <Typography variant="body2" sx={{ mt: 1 }}>{attendanceStats.rate}% Attendance Rate</Typography>
                </Box>
              </Box>
              <Box sx={{ width: 180, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={attendanceStats.chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                      <Cell key="present" fill="#4CAF50" />
                      <Cell key="absent" fill="#F44336" />
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </CardContent>
        </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Logistics Statistics</Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{logisticsStats.inUse}</Typography>
                <Typography color="text.secondary">In Use</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{logisticsStats.available}</Typography>
                <Typography color="text.secondary">Available</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{logisticsStats.fixing}</Typography>
                <Typography color="text.secondary">Fixing</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{logisticsStats.total}</Typography>
                <Typography color="text.secondary">Total</Typography>
              </Box>
              <Box sx={{ width: 220, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={logisticsStats.chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Bar dataKey="value" fill="#1976d2">
                      <Cell key="inUse" fill="#4CAF50" />
                      <Cell key="available" fill="#FFC107" />
                      <Cell key="fixing" fill="#F44336" />
                    </Bar>
                    <RechartsTooltip />
                    <Legend />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </CardContent>
        </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* WhatsApp Integration */}
      <WhatsAppIntegration
        currentLocation={currentLocation}
        currentTask={currentTask}
        userStatus={userStatus}
      />

      {/* Feedback FAB */}
      <Fab
        color="primary"
        aria-label="feedback"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setShowFeedbackDialog(true)}
      >
        <Feedback />
      </Fab>

      <Dialog open={showFeedbackDialog} onClose={() => setShowFeedbackDialog(false)}>
        <DialogTitle>Report Issue or Feedback</DialogTitle>
        <DialogContent>
          <textarea
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            placeholder="Tell us what happened..."
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              resize: 'vertical'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFeedbackDialog(false)}>Cancel</Button>
          <Button onClick={() => { // Placeholder for handleFeedbackSubmit
            setSnackbar({
              open: true,
              message: 'Thank you for your feedback! We\'ll review it promptly.',
              type: 'success'
            });
            setShowFeedbackDialog(false);
            setFeedbackMessage('');
          }} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 